import React, { useEffect, useState, useContext } from "react";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { PedidosVentaApi } from "../../api/PedidosVenta/PedidosVentaApi";
import {
  filterVentasIreneByUser,
  normalizeVentasIreneRows,
} from "../../utils/ventasIreneUtils";
import { filterMargenByUser } from "../../utils/margenesUtils";
import { mapUserToPermissionState } from "../../utils/userPermissionsUtils";
import Venta from "./Venta";
import VentaMes from "./VentaMes";
import VentaClienteAcumulado from "./VentaClienteAcumulado";
import VentaClienteMes from "./VentaClienteMes";
import MargenClienteAcumulado from "./MargenClienteAcumulado";
import MargenClienteMes from "./MargenClienteMes";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageHeroShellSx,
  pageFieldSx,
  pageSectionTitleSx,
  pageKpiChipSx,
  pageSectionDividerSx,
} from "../../styles/pageModernStyles";

const EMPRESA_LABELS = {
  stage: "STAGE",
  erna: "ERNA",
  fusion: "TOTAL (STAGE+ERNA)",
};

const DashboardPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { contentMargin } = useContext(SidebarContext);

  const [resumenRows, setResumenRows] = useState([]);
  const [margenRows, setMargenRows] = useState([]);
  const [canSeeMargenes, setCanSeeMargenes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(dayjs().year());
  const [empresa, setEmpresa] = useState("fusion");

  const pageShellSx = {
    ...contentMargin,
    p: { xs: 1, sm: 2 },
    pb: { xs: 10, sm: 3 },
    minHeight: "100vh",
    boxSizing: "border-box",
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem("token");
        const decoded = jwtDecode(token);
        const userEmail = decoded.email;
        const isAdmin = decoded.isAdmin;
        const permissions = mapUserToPermissionState(decoded);
        const showMargenes = isAdmin || permissions.Permiso_MargenesVenta;
        setCanSeeMargenes(showMargenes);

        const [ventasData, margenesData] = await Promise.all([
          PedidosVentaApi.getPedidosVentasIrene(empresa, year),
          showMargenes
            ? PedidosVentaApi.getPedidosComisionesReducido(empresa, year)
            : Promise.resolve([]),
        ]);

        const normalizedData = normalizeVentasIreneRows(ventasData, year);
        const filtered = filterVentasIreneByUser(
          normalizedData,
          userEmail,
          isAdmin,
        );

        setResumenRows(filtered);
        setMargenRows(
          filterMargenByUser(margenesData || [], userEmail, isAdmin),
        );
      } catch (err) {
        console.error("❌ Error cargando ventas dashboard:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year, empresa]);

  const renderSectionLabel = (label) => (
    <Box sx={pageSectionDividerSx(theme)}>
      <Typography sx={{ ...pageSectionTitleSx, whiteSpace: "nowrap" }}>
        {label}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={pageShellSx}>
        <Box
          sx={{
            minHeight: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.8),
              background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 58%)`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 28px rgba(0,0,0,0.35)"
                  : "0 10px 36px rgba(15,23,42,0.08)",
              minWidth: 220,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              Cargando ventas…
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageShellSx}>
      <Box sx={pageHeroShellSx(theme, { sticky: isMobile })}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          alignItems={{ xs: "flex-start", lg: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.5, sm: 2 } }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.14),
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <InsightsOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={pageSectionTitleSx}>Ventas · Dashboard</Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.2,
                  fontSize: { xs: "1.15rem", sm: "1.4rem" },
                }}
              >
                Panel de ventas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Evolución por comercial, cliente y márgenes.
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", lg: "auto" } }}
          >
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                label={EMPRESA_LABELS[empresa] || empresa}
                size="small"
                color="primary"
                variant="outlined"
                sx={pageKpiChipSx}
              />
              <Chip label={String(year)} size="small" variant="outlined" sx={pageKpiChipSx} />
            </Stack>

            <TextField
              select
              size="small"
              label="Empresa"
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value)}
              sx={{ ...pageFieldSx(theme), minWidth: { sm: 180 }, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <BusinessOutlinedIcon
                    fontSize="small"
                    sx={{ color: "text.secondary", mr: 0.5 }}
                  />
                ),
              }}
            >
              <MenuItem value="stage">STAGE</MenuItem>
              <MenuItem value="erna">ERNA</MenuItem>
              <MenuItem value="fusion">TOTAL (STAGE+ERNA)</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Año"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ ...pageFieldSx(theme), minWidth: { sm: 110 }, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <CalendarMonthOutlinedIcon
                    fontSize="small"
                    sx={{ color: "text.secondary", mr: 0.5 }}
                  />
                ),
              }}
            >
              {[...Array(6)].map((_, i) => {
                const y = dayjs().year() - i;
                return (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                );
              })}
            </TextField>
          </Stack>
        </Stack>
      </Box>

      {renderSectionLabel("Por comercial")}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Venta resumenRows={resumenRows} year={year} />
        </Grid>
        <Grid item xs={12} md={6}>
          <VentaMes resumenRows={resumenRows} year={year} />
        </Grid>
      </Grid>

      {renderSectionLabel("Por cliente")}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={6}>
          <VentaClienteAcumulado resumenRows={resumenRows} year={year} />
        </Grid>
        <Grid item xs={12} md={6}>
          <VentaClienteMes resumenRows={resumenRows} year={year} />
        </Grid>
      </Grid>

      {canSeeMargenes && (
        <>
          {renderSectionLabel("Márgenes")}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <MargenClienteAcumulado margenRows={margenRows} year={year} />
            </Grid>
            <Grid item xs={12} md={6}>
              <MargenClienteMes margenRows={margenRows} year={year} />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default DashboardPage;
